import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * A11Y-005 — ARIA live-region announcer.
 *
 * Provides a single `aria-live="polite"` region mounted at the Shell
 * level so any descendant — typically a TanStack Query `onError` — can
 * push a short message that screen readers will announce without
 * stealing focus.
 *
 * Why a small bespoke implementation (no library):
 *   - The BQB signature bans dependency creep for one-off helpers.
 *   - The pattern is 30 LoC of React + a `sr-only` div.
 *   - `aria-live="polite"` + `aria-atomic="true"` is enough for AT to
 *     re-announce the *entire* region whenever its text content
 *     changes, including identical messages bracketed by an empty
 *     reset (we toggle to "" briefly so repeated errors re-announce).
 *
 * Usage:
 *   const announce = useLiveAnnounce();
 *   announce('Could not save settings.');
 *
 * See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions.
 */

type Announcer = (message: string) => void;

const LiveAnnouncerContext = createContext<Announcer | null>(null);

interface ProviderProps {
  children: ReactNode;
}

/**
 * Why 200ms?
 *
 * The clear-then-set gap must exceed assistive-tech polling windows so
 * the region's text-node transition from "" back to the message is
 * actually observed by the screen reader. Empirically:
 *   - NVDA polls aria-live regions roughly every 100-250ms.
 *   - VoiceOver (macOS/iOS) polls around 150ms.
 *   - JAWS sits in a similar ~100-200ms range.
 * 60ms (our previous value) lands below NVDA's worst-case window, so
 * the toggle would frequently happen entirely between polls and the
 * announcement was dropped. 200ms safely clears every AT we target
 * (BUG-QAr8r4-3).
 */
const RESET_GAP_MS = 200;

export function LiveAnnouncerProvider({ children }: ProviderProps) {
  const [message, setMessage] = useState('');
  // Single source of truth for the announcer's in-flight state. We
  // queue rapid-fire announcements (BUG-QAr8r4-4): when announce() is
  // called while a timer is already pending, we append to `queue`
  // instead of cancelling the pending timer and overwriting the
  // message — which used to drop the first call of any rapid pair.
  const stateRef = useRef<{
    timer: number | null;
    queue: string[];
  }>({ timer: null, queue: [] });

  // Defined as a stable ref-free callback so the schedule chain below
  // and the public announce() share the same drain logic.
  const drainNext = useCallback(() => {
    const next = stateRef.current.queue.shift();
    if (next === undefined) {
      stateRef.current.timer = null;
      return;
    }
    setMessage('');
    stateRef.current.timer = window.setTimeout(() => {
      setMessage(next);
      // After painting the message, schedule another clear-then-set
      // cycle for the next queued item (if any). This guarantees AT
      // sees a distinct text-node transition for every queued message.
      stateRef.current.timer = window.setTimeout(() => {
        drainNext();
      }, RESET_GAP_MS);
    }, RESET_GAP_MS);
  }, []);

  const announce = useCallback<Announcer>(
    (next) => {
      const trimmed = (next ?? '').toString().trim();
      if (!trimmed) return;
      stateRef.current.queue.push(trimmed);
      // If no announcement is currently in flight, kick off the
      // drain. Otherwise the in-flight timer chain will pick this up
      // on its next tick.
      if (stateRef.current.timer === null) {
        drainNext();
      }
    },
    [drainNext],
  );

  useEffect(
    () => () => {
      if (stateRef.current.timer !== null) {
        window.clearTimeout(stateRef.current.timer);
        stateRef.current.timer = null;
      }
      stateRef.current.queue.length = 0;
    },
    [],
  );

  // `announce` is already stable via useCallback, so wrapping it in
  // useMemo was a no-op identity wrap (BUG-QAr8r4-5). Pass directly.
  return (
    <LiveAnnouncerContext.Provider value={announce}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {message}
      </div>
    </LiveAnnouncerContext.Provider>
  );
}

/**
 * Hook — returns a stable `announce(message)` function. Safe to call
 * outside the provider; it becomes a no-op so feature code never
 * crashes (e.g. unit tests that mount a single page without the
 * Shell).
 */
export function useLiveAnnounce(): Announcer {
  const ctx = useContext(LiveAnnouncerContext);
  // Stable noop reference — avoids re-renders downstream when the
  // provider isn't mounted (tests, isolated stories).
  return ctx ?? noopAnnounce;
}

const noopAnnounce: Announcer = () => {
  /* no-op when used outside the provider */
};
